import { FormEvent, useEffect, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import AuthCard from "../../../../shared/components/AuthCard";
import Button from "../../../../shared/components/Button";
import PasswordInput from "../../../../shared/components/PasswordInput";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { isValidCpf } from "../../../../shared/utils/cpf";
import { useAuth } from "../../shared/hooks/useAuth";
import { isValidPhoneNumber } from "libphonenumber-js";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import {
  AuthUser,
  USER_CATEGORY_VALUES,
  userCategoryLabel,
} from "../../shared/types/auth";

export default function RegisterPage() {
  const { signUp, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [sex, setSex] = useState<AuthUser["sex"]>("");
  const [category, setCategory] = useState<AuthUser["category"]>("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    window.location.hash = "#/perfil";
  }, [user]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const normalizedCpf = String(cpf || "").replace(/\D/g, "");
    if (!isValidCpf(normalizedCpf)) {
      setError(siteTexts.auth.register.invalidCpf);
      return;
    }
    const normalizedPhone = String(phone || "").trim();
    if (!normalizedPhone || !isValidPhoneNumber(normalizedPhone)) {
      setError(siteTexts.auth.register.invalidPhone);
      return;
    }
    try {
      await signUp(name, email, password, normalizedCpf, normalizedPhone, sex, category);
      window.location.hash = "#/perfil";
    } catch (_err) {
      setError(siteTexts.auth.register.error);
    }
  };

  return (
    <AppLayout
      title={siteTexts.auth.register.title}
      subtitle={siteTexts.auth.register.subtitle}
    >
      <AuthCard
        error={error}
        onSubmit={onSubmit}
        submitAction={
          <Button type="submit" variant="primary">
            {siteTexts.auth.register.submit}
          </Button>
        }
        footer={
          <p>
            {siteTexts.auth.register.footerCta}{" "}
            <a href="#/login">{siteTexts.auth.register.footerLink}</a>
          </p>
        }
      >
        <label className="field">
          <span>{siteTexts.auth.register.nameLabel}</span>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{siteTexts.auth.register.emailLabel}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{siteTexts.auth.register.cpfLabel}</span>
          <input
            type="text"
            value={cpf}
            onChange={(event) => setCpf(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{siteTexts.auth.register.phoneLabel}</span>
          <PhoneInput
            defaultCountry="br"
            value={phone}
            onChange={(value) => setPhone(value)}
            placeholder="+55 (11) 99999-9999"
            required
          />
        </label>
        <label className="field">
          <span>{siteTexts.auth.register.sexLabel}</span>
          <select
            value={sex}
            onChange={(event) => setSex(event.target.value as AuthUser["sex"])}
          >
            <option value="">{siteTexts.common.sex.notInformed}</option>
            <option value="female">{siteTexts.common.sex.female}</option>
            <option value="male">{siteTexts.common.sex.male}</option>
            <option value="other">{siteTexts.common.sex.other}</option>
          </select>
        </label>
        <label className="field">
          <span>{siteTexts.auth.register.categoryLabel}</span>
          <select
            value={category}
            onChange={(event) =>
              setCategory(event.target.value as AuthUser["category"])
            }
          >
            {USER_CATEGORY_VALUES.map((item) => (
              <option key={`register-category-${item || "empty"}`} value={item}>
                {item ? userCategoryLabel(item) : siteTexts.common.category.notInformed}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{siteTexts.auth.register.passwordLabel}</span>
          <PasswordInput
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
      </AuthCard>
    </AppLayout>
  );
}
